// @architect-approved: INFRASTRUCTURE - React context provider, not a registered @tg-seq component — the "Self" component identity pattern does not apply here.
// @architect-approved: HARDCODED_COLORS - This hook IS the canonical single source of
// default/fallback brand color values (used before the org's live branding record loads).
// These literals are the designated home for them, matching the MUI default palette
// (primary.main #1976d2, etc.) intentionally - nothing else in the codebase should
// inline these, they should call useBrandingContext() instead.

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import tsxInventory from '../../../public/tsx-inventory-manifest.json';
import cabooseLogo from '../../TGC/img/bg_tgc_brand_caboose.png';
import { SecureStorage } from '../../utils/SecureStorage';
import { apiRequest } from '../../utils/apiService';
import { captureRouteContext } from '../../utils/routeContext';
import { useAuth } from '../../utils/useAuth';

/**
 * Brand Collection Interface - Audited "Skinny" White-Label Variables
 *
 * Intelligent Branding Strategy (User Association-Aware):
 * 1. Check user's org associations (status=3 = confirmed/active)
 * 2. If user has NO associations → Use system default (UUID: 00000000-0000-0000-0000-000000000000)
 * 3. If user has MULTIPLE associations → Use domain default (TGC/LCV based on component context)
 * 4. If user has SINGLE association OR explicit org filter set → Try org-specific collection, fall back to domain default
 * 5. Hardcoded template defaults (absolute fallback if all else fails)
 *
 * Total: ~19 variables control entire white-label experience
 */
export interface BrandCollection {
    // Core Identity (7 variables)
    orgId: string; // UUID format
    collectionName: string;
    collectionDescription?: string;
    primaryColor: string;
    secondaryColor: string;
    logoUrl: string;
    officePhone: string;
    helpEmail: string;
    menuTextColor: 'light' | 'dark';

    // Typography (5 variables)
    fontFamilyPrimary?: string;
    fontFamilySecondary?: string;
    fontWeightHeadline?: number;
    fontWeightBody?: number;
    fontSizeBase?: string;

    // Semantic Colors (4 variables) - alerts/validation/status, distinct from primary/secondary brand colors
    errorColor?: string;
    warningColor?: string;
    infoColor?: string;
    successColor?: string;

    // Temporal (3 variables)
    seasonalTheme?: string | null;
    activeFrom: string;
    activeTo?: string | null;

    // Architecture (5 variables)
    domain?: 'TGC' | 'LCV' | 'PUBLIC';
    tier?: 'domain' | 'dirm' | 'org';
    parentOrgId?: string | null; // UUID format
    isTenantCustomizable?: boolean;
    notes?: string;

    // Development Tools Colors (8 variables) - For validator dashboards, dev tools, status indicators
    devColorPhase1?: string; // IDE-Level Validation
    devColorPhase2?: string; // Python Infrastructure
    devColorPhase3?: string; // Navigation Enforcement
    devColorPhase4?: string; // Component Validation
    devColorPhase5?: string; // Backend Enforcement
    devColorPhase6?: string; // Frontend Enforcement
    devColorPhase7?: string; // Build Artifacts
    devColorPhase8?: string; // Production Validation

    // Audit Trail
    createdBy: number;
    lastModified: string;
}

// Context State Interface
interface BrandingContextState {
    collection: BrandCollection | null;
    loading: boolean;
    error: string | null;
    refreshCollection: () => Promise<void>;
    applyCollection: (collection: BrandCollection) => void;
}

// Create Context
const BrandingContext = createContext<BrandingContextState | undefined>(undefined);

/**
 * BrandingProvider - Wraps app to provide branding context
 * @param children - React children components
 * @param orgId - Organization UUID (string)
 *
 * Usage:
 * <BrandingProvider orgId={userContext.currentOrg.uuid}>
 *   <App />
 * </BrandingProvider>
 */
export const BrandingProvider: React.FC<{ children: React.ReactNode; orgId: string }> = ({
    children,
    orgId,
}) => {
    const [collection, setCollection] = useState<BrandCollection | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // 🔒 ARCHITECTURAL PATTERN: Read from Redux (NO API call)
    // - PlatformHeader loads org associations ONCE → Redux
    // - This hook reads from Redux via useAuth()
    // - NO duplicate API calls (one-way dependency flow)
    const { userOrganizations } = useAuth();

    /**
     * Detect domain for the currently active component.
     *
     * FIX (2026-09-29): The platform migrated to sequence-based direct routes
     * (`/component/{seq}`, see routeContext.ts) a while back. This function's
     * ORIGINAL implementation matched literal `_tgc_`/`_lcv_`/`/tgc/`/`/lcv/`
     * substrings in window.location.pathname - a pattern that can never appear
     * in a `/component/10024`-style URL. Net effect: detectDomain() always
     * fell through to 'PUBLIC' for every route in the app, silently breaking
     * the TGC/LCV brand-switch (conference rooms, directories, etc.) without
     * throwing - it just always returned the wrong (but valid-looking) answer.
     *
     * NEW STRATEGY: resolve the active module's manifest entry via its route
     * sequence number (authoritative - generate-tsx-inventory.js already
     * tags every component with its true domain) and only fall back to the
     * legacy string-matching for routes with no resolvable seq (e.g. truly
     * static/public marketing pages that never go through /component/:seq).
     */
    const detectDomain = (): 'TGC' | 'LCV' | 'PUBLIC' => {
        const { moduleSeq } = captureRouteContext();

        if (moduleSeq !== null) {
            const entry = (tsxInventory.data as any[]).find(
                (e: any) => e.sequence_num === moduleSeq,
            );
            if (entry?.domain === 'TechgacityCenter') {
                return 'TGC';
            }
            if (entry?.domain === 'LifeCenterVillage') {
                return 'LCV';
            }
            if (entry?.domain === 'MainStreet' || entry?.domain === 'Other') {
                return 'PUBLIC';
            }
            // Fall through to legacy path matching below only if the seq
            // resolved to no manifest entry at all (shouldn't normally happen).
        }

        // Legacy fallback: literal path segments (pre-sequence-routing URLs,
        // or any route that never resolves a moduleSeq)
        const path = window.location.pathname.toLowerCase();
        if (path.match(/_(lcv)_\d/) || path.includes('/lcv/')) {
            return 'LCV';
        }
        if (path.match(/_(tgc)_\d/) || path.includes('/tgc/')) {
            return 'TGC';
        }

        return 'PUBLIC';
    };

    /**
     * Fetch active collection using intelligent cascading strategy
     *
     * Intelligent Branding Strategy (User Association-Aware):
     * 1. Check user's org associations (status=3 = confirmed/active)
     * 2. Apply cascading logic:
     *    - NO associations (0) → Use system default UUID (00000000...00000)
     *    - MULTIPLE associations (>1) → Use domain default (TGC/LCV) - skip org-specific lookup
     *    - SINGLE association (1) → Try org-specific, fall back to domain default
     *    - EXPLICIT org filter set → Try org-specific, fall back to domain default
     * 3. Lookup order for org-specific:
     *    a. Universal manifest (/branding/manifest.json) - filter by orgId + domain
     *    b. Domain-specific default (TGC/LCV/PUBLIC) - local static fallback
     *    c. Hardcoded default - absolute fallback
     *
     * Future Phase (Tenant Customization):
     *   - Org edits branding via UI (BRAND.Branding_Config CRUD)
     *   - Export to tenant-orgs/{uuid}/.metadata/branding.json (blob storage)
     */
    const fetchCollection = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Detect domain from component filename pattern (scr_tgc_, scr_lcv_)
            const domain = detectDomain();

            // STEP 0: Check user's org associations from Redux (for logging/debugging only)
            // 🔒 ONE-WAY DEPENDENCY: Read from Redux (NO API call)
            // Logic:
            // - 0 associations = User not affiliated with any org
            // - 1 association = User works for single org (use org-specific branding)
            // - >1 associations = Multi-org user (use domain default to avoid ambiguity)
            const associationCount = userOrganizations.length;
            console.log(`ℹ️ User has ${associationCount} org associations (from Redux)`);

            // STEP 1: Determine effective orgId
            const effectiveOrgId = orgId;
            let shouldTryOrgSpecific = true;

            // If no orgId provided OR system default UUID → skip org-specific lookup
            if (!orgId || orgId === '00000000-0000-0000-0000-000000000000') {
                console.log(
                    `ℹ️ No specific org selected - using ${domain} domain default branding`,
                );
                shouldTryOrgSpecific = false;
            } else {
                // orgId IS provided → try org-specific branding (regardless of association count)
                console.log(
                    `ℹ️ Org selected: ${effectiveOrgId} - attempting org-specific branding for ${domain}`,
                );
                shouldTryOrgSpecific = true;
            }

            // STEP 2: Try org-specific branding (if orgId provided)
            if (shouldTryOrgSpecific) {
                try {
                    const manifestResponse = await fetch('/branding/manifest.json', {
                        method: 'GET',
                        headers: { Accept: 'application/json' },
                        cache: 'default',
                    });

                    if (manifestResponse.ok) {
                        const manifest = await manifestResponse.json();

                        // Find collection matching BOTH orgId AND domain
                        const collection = manifest.collections.find(
                            (c: BrandCollection) =>
                                c.orgId === effectiveOrgId && c.domain === domain,
                        );

                        if (collection) {
                            console.log(
                                `✅ Loaded ${domain} org-specific branding (orgId=${effectiveOrgId})`,
                            );
                            applyCollection(collection);
                            setCollection(collection);
                            setLoading(false);
                            return;
                        }
                        console.log(
                            `ℹ️ No org-specific collection found for orgId=${effectiveOrgId}, using ${domain} domain default`,
                        );
                    }
                } catch (err) {
                    console.warn('Universal manifest unavailable, using domain default...', err);
                }
            }

            // STEP 3: Fallback to domain-specific local JSON (platform defaults)
            let collectionData: BrandCollection;

            try {
                if (domain === 'LCV') {
                    const lcvCollection = await import('../collections/lcv-default.json');
                    collectionData = {
                        ...(lcvCollection.default as BrandCollection),
                        logoUrl: cabooseLogo,
                    };
                } else if (domain === 'TGC') {
                    const tgcCollection = await import('../collections/tgc-default.json');
                    collectionData = {
                        ...(tgcCollection.default as BrandCollection),
                        logoUrl: cabooseLogo,
                    };
                } else {
                    // PUBLIC domain - use default
                    const defaultCollection = await import('../collections/default.json');
                    collectionData = {
                        ...(defaultCollection.default as BrandCollection),
                        logoUrl: cabooseLogo,
                    };
                }
                console.log(`✅ Loaded ${domain} domain collection from local JSON`);
            } catch (importErr) {
                // STEP 4: Absolute fallback to default.json
                console.warn(
                    `Failed to load ${domain} collection, falling back to default`,
                    importErr,
                );
                const defaultCollection = await import('../collections/default.json');
                collectionData = {
                    ...(defaultCollection.default as BrandCollection),
                    logoUrl: cabooseLogo,
                };
            }

            // Apply collection to document (inject CSS variables)
            applyCollection(collectionData);

            setCollection(collectionData);
        } catch (err) {
            console.error('❌ Failed to load brand collection:', err);
            setError('Failed to load branding configuration');

            // Hardcoded fallback if all else fails
            const fallback: BrandCollection = {
                orgId: '00000000-0000-0000-0000-000000000000', // Default fallback UUID
                collectionName: 'Techgacity Default',
                primaryColor: '#1976d2',
                secondaryColor: '#dc004e',
                logoUrl: cabooseLogo,
                officePhone: '(800) 555-TECH',
                helpEmail: 'support@mytechgacity.com',
                menuTextColor: 'light',
                seasonalTheme: null,
                activeFrom: '2024-01-01T00:00:00Z',
                activeTo: null,
                parentOrgId: null,
                createdBy: 1,
                lastModified: '2026-02-10T00:00:00Z',
                errorColor: '#d32f2f',
                warningColor: '#ed6c02',
                infoColor: '#0288d1',
                successColor: '#2e7d32',
                fontSizeBase: '16px',
                devColorPhase1: '#1976d2',
                devColorPhase2: '#388e3c',
                devColorPhase3: '#f57c00',
                devColorPhase4: '#7b1fa2',
                devColorPhase5: '#c62828',
                devColorPhase6: '#0097a7',
                devColorPhase7: '#5d4037',
                devColorPhase8: '#00695c',
            };
            applyCollection(fallback);
            setCollection(fallback);
        } finally {
            setLoading(false);
        }
    }, [orgId, userOrganizations.length]); // userOrganizations only used for its count (logging) —
    // depending on .length instead of the array reference avoids re-creating
    // this callback (and re-firing the effect below) every time Redux's
    // setUserOrganizations reducer produces a new array reference with the
    // SAME contents, which was causing a fetch-collection retry loop.

    /**
     * Apply collection to document root as CSS custom properties
     * This makes variables available globally via var(--brand-*)
     *
     * FIX (2026-09-29): wrapped in useCallback - this is exposed through the
     * context value below, so a fresh function reference on every render was
     * one of the causes of the value object never being stable (see value
     * useMemo comment).
     */
    const applyCollection = useCallback((coll: BrandCollection) => {
        const root = document.documentElement;

        // Inject CSS variables (from brand-emulator.css template)
        root.style.setProperty('--brand-primary-color', coll.primaryColor);
        root.style.setProperty('--brand-secondary-color', coll.secondaryColor);
        root.style.setProperty('--brand-logo-url', `url(${coll.logoUrl})`);
        root.style.setProperty('--brand-office-phone', coll.officePhone);
        root.style.setProperty('--brand-help-email', coll.helpEmail);
        root.style.setProperty(
            '--brand-menu-text-color',
            coll.menuTextColor === 'light' ? '#ffffff' : '#000000',
        );

        // Semantic colors + base font size (optional - only present once a collection
        // has been backfilled with these GBRAND 'global' section variables)
        if (coll.errorColor) root.style.setProperty('--brand-error-color', coll.errorColor);
        if (coll.warningColor) root.style.setProperty('--brand-warning-color', coll.warningColor);
        if (coll.infoColor) root.style.setProperty('--brand-info-color', coll.infoColor);
        if (coll.successColor) root.style.setProperty('--brand-success-color', coll.successColor);
        if (coll.fontSizeBase) root.style.setProperty('--brand-font-size-base', coll.fontSizeBase);

        // Seasonal theme (optional CSS class for special styling)
        if (coll.seasonalTheme) {
            root.setAttribute('data-seasonal-theme', coll.seasonalTheme);
        } else {
            root.removeAttribute('data-seasonal-theme');
        }

        // Development Tools Colors (for validator dashboards, dev tools, status indicators)
        //
        // NOTE (2026-10-30): these 8 fields are a legacy path -- the canonical source for
        // dev/architect-tooling colors is now the `utilityColors` section of
        // public/branding/manifest.json (see UTILITY_COLORS in generate-branding-manifests.js
        // and src/utils/useUtilityColors.ts). Org-specific collections fetched from that same
        // manifest never carry devColorPhaseN fields, so these conditional sets are frequently
        // no-ops; left as a harmless fallback for the 3 hardcoded domain-default JSON
        // collections that still define them, but new dev-tool dashboards should consume
        // useUtilityColors() instead of var(--dev-color-phaseN).
        if (coll.devColorPhase1) root.style.setProperty('--dev-color-phase1', coll.devColorPhase1);
        if (coll.devColorPhase2) root.style.setProperty('--dev-color-phase2', coll.devColorPhase2);
        if (coll.devColorPhase3) root.style.setProperty('--dev-color-phase3', coll.devColorPhase3);
        if (coll.devColorPhase4) root.style.setProperty('--dev-color-phase4', coll.devColorPhase4);
        if (coll.devColorPhase5) root.style.setProperty('--dev-color-phase5', coll.devColorPhase5);
        if (coll.devColorPhase6) root.style.setProperty('--dev-color-phase6', coll.devColorPhase6);
        if (coll.devColorPhase7) root.style.setProperty('--dev-color-phase7', coll.devColorPhase7);
        if (coll.devColorPhase8) root.style.setProperty('--dev-color-phase8', coll.devColorPhase8);

        // Log for debugging
        console.log('✅ Brand collection applied:', coll.collectionName);
    }, []);

    /**
     * Refresh collection (e.g., after user switches org or date changes)
     */
    const refreshCollection = useCallback(async () => {
        await fetchCollection();
    }, [fetchCollection]);

    // Fetch collection on mount and when orgId changes
    useEffect(() => {
        fetchCollection();
    }, [fetchCollection]);

    // FIX (2026-09-29): memoize the context value. Previously this was a
    // fresh object literal (with a fresh refreshCollection closure) on EVERY
    // BrandingProvider render. BrandingProvider wraps <BrowserRouter>
    // <AppContent/></BrowserRouter> directly in App.tsx with no props
    // boundary, so every BrandingProvider render (e.g. from fetchCollection's
    // setLoading/setCollection calls) forced the ENTIRE route tree
    // (AppContent/AuthGuard/SequenceRouteLoader) to re-render too - turning
    // any upstream retry churn (e.g. the org-association fetch failing) into
    // an app-wide render storm instead of a localized one. See
    // AppContent's React.memo wrap in App.tsx for the other half of this fix.
    const value = useMemo<BrandingContextState>(
        () => ({
            collection,
            loading,
            error,
            refreshCollection,
            applyCollection,
        }),
        [collection, loading, error, refreshCollection, applyCollection],
    );

    return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

/**
 * useBrandingContext - Hook to access brand collection in components
 *
 * Usage:
 * const { collection, loading, error, refreshCollection } = useBrandingContext();
 *
 * if (loading) return <Spinner />;
 * if (error) return <Error message={error} />;
 *
 * return (
 *   <Box sx={{ backgroundColor: collection.primaryColor }}>
 *     <img src={collection.logoUrl} alt="Logo" />
 *     <a href={`tel:${collection.officePhone}`}>{collection.officePhone}</a>
 *   </Box>
 * );
 */
export const useBrandingContext = (): BrandingContextState => {
    const context = useContext(BrandingContext);

    if (!context) {
        throw new Error('useBrandingContext must be used within BrandingProvider');
    }

    return context;
};

// Export type for external use
export type { BrandingContextState };
